export const resizePhoto = async ({
  file,
  maxWidth = 1080,
  maxHeight = 1080,
  quality = 0.75,
}: {
  file: File;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
}): Promise<Blob> => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const img = await createImageBitmap(file);

  // calculate new size
  const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
  const width = (img.width * ratio + 0.5) | 0;
  const height = (img.height * ratio + 0.5) | 0;

  // resize the canvas to the new dimensions
  canvas.width = width;
  canvas.height = height;

  // scale & draw the image onto the canvas
  ctx?.drawImage(img, 0, 0, width, height);

  //get the blob from the resize image
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject('Can not generate blob')), 'image/webp', quality)
  );
};

/**
 * Crop image to specific aspect ratio
 * @see https://pqina.nl/blog/cropping-images-to-an-aspect-ratio-with-javascript/
 * @param url
 * @param aspectRatio
 * @param quality
 */
export const cropPhoto = async (url: string, aspectRatio = 1, quality = 0.75): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    // this image will hold our source image data
    const inputImage = new Image();

    // we want to wait for our image to load
    inputImage.onload = () => {
      // let's store the width and height of our image
      const inputWidth = inputImage.naturalWidth;
      const inputHeight = inputImage.naturalHeight;

      // get the aspect ratio of the input image
      const inputImageAspectRatio = inputWidth / inputHeight;

      // if it's bigger than our target aspect ratio
      let outputWidth = inputWidth;
      let outputHeight = inputHeight;
      if (inputImageAspectRatio > aspectRatio) {
        outputWidth = inputHeight * aspectRatio;
      } else if (inputImageAspectRatio < aspectRatio) {
        outputHeight = inputWidth / aspectRatio;
      }

      // calculate the position to draw the image at
      const outputX = (outputWidth - inputWidth) * 0.5;
      const outputY = (outputHeight - inputHeight) * 0.5;

      // create a canvas that will present the output image
      const outputImage = document.createElement('canvas');

      // set it to the same size as the image
      outputImage.width = outputWidth;
      outputImage.height = outputHeight;

      // draw our image at position 0, 0 on the canvas
      const ctx = outputImage.getContext('2d');
      ctx?.drawImage(inputImage, outputX, outputY);

      //get the blob from the resize image
      outputImage.toBlob((blob) => (blob ? resolve(blob) : reject('Can not generate blob')), 'image/webp', quality);
    };

    // start loading our image
    inputImage.src = url;
  });
};

export const fileToDataUrl = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader?.result as string);
    reader.onerror = reject;
    reader.onabort = reject;
    reader.readAsDataURL(blob);
  });
};

export const createImageBitmap = async (data: Blob): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const dataUrl = URL.createObjectURL(data);

    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
};
